import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * MarketplaceFooter — premium sprzedażowa stopka ApisAI.
 *
 * Układ:
 *   1. CTA HERO — "Gotów, żeby klienci sami trafiali..." + Dodaj ogłoszenie
 *   2. Logo + trust 3 checkmarki
 *   3. 3 główne kolumny: Marketplace / Dla hodowcy / Informacje
 *   4. Collapsible: Popularne rasy + Popularne miasta (pod kolumnami, zwinięte dla SEO)
 *   5. Bottom copyright strip
 *
 * JEDEN EKOSYSTEM: brak publicznych formularzy. CTA „Dodaj ogłoszenie" prowadzi do Panelu APISAI
 * (gość → /login, zalogowany → /panel), gdzie hodowca używa właściwych kreatorów.
 */

const POPULAR_BREEDS = [
  { name: 'Owczarek niemiecki', slug: 'owczarek-niemiecki' },
  { name: 'Labrador retriever', slug: 'labrador-retriever' },
  { name: 'Golden retriever', slug: 'golden-retriever' },
  { name: 'Maltańczyk', slug: 'maltanczyk' },
  { name: 'Yorkshire terrier', slug: 'yorkshire-terrier' },
  { name: 'Cavalier king charles spaniel', slug: 'cavalier-king-charles-spaniel' },
  { name: 'Border collie', slug: 'border-collie' },
  { name: 'Shih tzu', slug: 'shih-tzu' },
  { name: 'Bulldog francuski', slug: 'bulldog-francuski' },
  { name: 'Chihuahua', slug: 'chihuahua' },
  { name: 'Beagle', slug: 'beagle' },
  { name: 'Husky syberyjski', slug: 'husky-syberyjski' },
  { name: 'Pomeranian', slug: 'pomeranian' },
  { name: 'Cane corso', slug: 'cane-corso' },
  { name: 'Welsh corgi pembroke', slug: 'welsh-corgi-pembroke' },
  { name: 'Akita inu', slug: 'akita-inu' },
];

const POPULAR_CITIES = [
  { name: 'Warszawa', slug: 'warszawa' }, { name: 'Kraków', slug: 'krakow' },
  { name: 'Wrocław', slug: 'wroclaw' }, { name: 'Poznań', slug: 'poznan' },
  { name: 'Gdańsk', slug: 'gdansk' }, { name: 'Łódź', slug: 'lodz' },
  { name: 'Katowice', slug: 'katowice' }, { name: 'Lublin', slug: 'lublin' },
  { name: 'Bydgoszcz', slug: 'bydgoszcz' }, { name: 'Szczecin', slug: 'szczecin' },
  { name: 'Białystok', slug: 'bialystok' }, { name: 'Gdynia', slug: 'gdynia' },
  { name: 'Częstochowa', slug: 'czestochowa' }, { name: 'Rzeszów', slug: 'rzeszow' },
  { name: 'Toruń', slug: 'torun' }, { name: 'Kielce', slug: 'kielce' },
];

export default function MarketplaceFooter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // JEDEN EKOSYSTEM: brak publicznych formularzy — CTA prowadzi do Panelu/logowania.
  const goAdd = (e) => { e.preventDefault(); navigate(user ? '/panel' : '/login'); };
  return (
    <footer
      className="mt-20 border-t border-white/10 bg-gradient-to-b from-black/40 to-black/80 backdrop-blur-sm"
      data-testid="marketplace-footer"
    >
      <div className="max-w-6xl mx-auto px-6 py-10 md:py-16">

        {/* ============== 1. CTA HERO ============== */}
        <div
          className="relative overflow-hidden rounded-2xl md:rounded-3xl border border-orange-500/20 bg-gradient-to-br from-orange-500/10 via-rose-500/5 to-transparent p-6 md:p-10 mb-12 md:mb-16"
          data-testid="footer-cta-hero"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 leading-tight">
                Gotów, żeby klienci sami<br className="hidden md:inline" /> trafiali do Twojej hodowli?
              </h2>
              <p className="text-sm md:text-base text-white/70">
                Dodaj ogłoszenie i zacznij zbierać zapytania jeszcze dziś.
              </p>
            </div>
            <button
              type="button"
              onClick={goAdd}
              data-testid="footer-cta-add-listing"
              className="group shrink-0 inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3.5 md:py-4 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-black text-sm md:text-base font-bold transition shadow-2xl shadow-orange-500/30 hover:scale-[1.02]"
            >
              <span className="text-lg">➕</span>
              Dodaj ogłoszenie
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </button>
          </div>
        </div>

        {/* ============== 2. BRANDING + 3 COLUMN GRID ============== */}
        <div className="grid gap-8 md:gap-12 md:grid-cols-4">
          {/* Brand column */}
          <div data-testid="footer-brand">
            <Link to="/" className="inline-flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center font-bold text-white">A</div>
              <span className="text-lg font-bold text-white">ApisAI</span>
              <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            </Link>
            <p className="text-sm text-white/60 mb-4 leading-relaxed">
              Marketplace dla hodowców psów rasowych.
            </p>
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2 text-xs text-white/60">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                Bezpośredni kontakt z hodowcą
              </li>
              <li className="flex items-center gap-2 text-xs text-white/60">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                Sprawdzone ogłoszenia
              </li>
              <li className="flex items-center gap-2 text-xs text-white/60">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                Cała Polska
              </li>
            </ul>
          </div>

          {/* Col: Marketplace */}
          <div data-testid="footer-marketplace">
            <div className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <span>🐶</span> Marketplace
            </div>
            <ul className="space-y-2.5">
              <li><Link to="/katalog?type=mioty" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-szczenieta">Szczenięta</Link></li>
              <li><Link to="/katalog?type=hodowle" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-hodowle">Hodowle</Link></li>
              <li><Link to="/katalog?type=reproduktory" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-reproduktory">Reproduktory</Link></li>
              <li><Link to="/rasy" className="text-sm text-white/60 hover:text-white transition-colors">Katalog ras</Link></li>
              <li><Link to="/blog" className="text-sm text-white/60 hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Col: Dla hodowcy */}
          <div data-testid="footer-breeder">
            <div className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <span>👤</span> Dla hodowcy
            </div>
            <ul className="space-y-2.5">
              <li>
                <button
                  type="button" onClick={goAdd}
                  className="text-sm text-orange-300 hover:text-orange-200 font-semibold transition-colors"
                  data-testid="footer-link-dodaj"
                >
                  ➕ Dodaj ogłoszenie
                </button>
              </li>
              <li><Link to="/dla-hodowcy" className="text-sm text-white/60 hover:text-white transition-colors">Jak to działa</Link></li>
              <li><Link to="/panel" className="text-sm text-white/60 hover:text-white transition-colors">Panel hodowcy</Link></li>
            </ul>
          </div>

          {/* Col: Informacje */}
          <div data-testid="footer-info">
            <div className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <span>ℹ️</span> Informacje
            </div>
            <ul className="space-y-2.5">
              <li><Link to="/kontakt" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-kontakt">Kontakt</Link></li>
              <li><Link to="/regulamin" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-regulamin">Regulamin</Link></li>
              <li><Link to="/polityka-prywatnosci" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-link-polityka">Polityka prywatności</Link></li>
            </ul>
          </div>
        </div>

        {/* ============== 3. Popularne rasy / miasta — ZWIJANE (SEO) ============== */}
        <div className="mt-12 space-y-3 border-t border-white/5 pt-8">
          <details className="group" data-testid="footer-breeds-collapsible">
            <summary className="flex items-center justify-between py-2 cursor-pointer list-none select-none text-sm font-semibold text-white/80 hover:text-white transition">
              <span>Szczenięta według rasy</span>
              <ChevronDown className="w-4 h-4 text-white/40 transition-transform group-open:rotate-180" />
            </summary>
            <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
              {POPULAR_BREEDS.map(b => (
                <li key={b.slug}>
                  <Link
                    to={`/rasy/${b.slug}`}
                    className="text-xs text-white/50 hover:text-white transition-colors"
                    data-testid={`footer-breed-${b.slug}`}
                  >
                    {b.name}
                  </Link>
                </li>
              ))}
              <li className="col-span-2 md:col-span-4 pt-1">
                <Link to="/rasy" className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1">
                  Wszystkie rasy →
                </Link>
              </li>
            </ul>
          </details>

          <details className="group" data-testid="footer-cities-collapsible">
            <summary className="flex items-center justify-between py-2 cursor-pointer list-none select-none text-sm font-semibold text-white/80 hover:text-white transition">
              <span>Popularne miasta</span>
              <ChevronDown className="w-4 h-4 text-white/40 transition-transform group-open:rotate-180" />
            </summary>
            <ul className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
              {POPULAR_CITIES.map(c => (
                <li key={c.slug}>
                  <Link
                    to={`/katalog?miasto=${encodeURIComponent(c.name)}`}
                    className="text-xs text-white/50 hover:text-white transition-colors"
                    data-testid={`footer-city-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
        </div>

        {/* ============== 4. Bottom strip ============== */}
        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} ApisAI Marketplace · Kontakt trafia bezpośrednio do hodowcy
          </p>
          <Link to="/kontakt" className="text-xs text-white/40 hover:text-white transition">
            Masz pytania? Napisz do nas →
          </Link>
        </div>

        {/* Made with love credit */}
        <div className="mt-4 pt-4 border-t border-white/5 text-center" data-testid="footer-credit">
          <p className="text-[11px] text-white/30 inline-flex items-center gap-1.5 justify-center">
            Made with <span className="text-rose-400 animate-pulse">❤</span> by{' '}
            <a
              href="https://apisdesign.online"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white font-medium transition underline underline-offset-2 decoration-white/20 hover:decoration-white/60"
            >
              apisdesign.online
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
